<script setup lang="ts">
import {
  CreateRole,
  CreateRoleProfile,
  CreateUserPermission,
  DeleteRoleProfile,
  DeleteUserPermission,
  GetCurrentAccess,
  GetPermissionManagerOptions,
  GetPermissionManagerPermissions,
  GetPageAccessSettings,
  GetRoleProfiles,
  GetRoles,
  GetUserPermissions,
  GetUsers,
  AddPermissionManagerRule,
  RemovePermissionManagerRule,
  ResetPermissionManagerRules,
  SyncFrontendPages,
  UpdatePermissionManagerRule,
  UpdatePageAccessSettings,
  UpdateRoleProfile,
  UpdateUserRoleAssignments,
  type CreateRolePayload,
  type CreateUserPermissionPayload,
  type FrappeDocTypePermission,
  type FrontendPageAccessPayload,
  type PageAccessSetting,
  type FrappeRole,
  type FrappeRoleProfile,
  type FrappeUser,
  type FrappeUserPermission,
  type PermissionManagerOption,
  type PermissionManagerPermissionKey,
} from "@/api/permissionsApi";
import {
  searchFrappeDoctype,
  type FrappeDoctypeLookupOption,
} from "@/api/frappeLookupApi";
import FrappeDoctypeAutocomplete from "@/components/shared/FrappeDoctypeAutocomplete.vue";
import { hasFullAccessRole, permissionPages } from "@/config/permissionManifest";
import useToastify from "@/stores/toastify.store";
import { useAuthStore } from "@/stores/auth.store";
import type { AccessSnapshot } from "@/type/permissions";
import type { Ref } from "vue";
import { useI18n } from "vue-i18n";

definePage({
  meta: {
    action: "read",
    subject: "Access Management",
  },
});

type AccessTab = "users" | "role-profiles" | "roles" | "page-access" | "role-permissions" | "user-permissions" | "effective-access";
type TablePermissionRow = FrappeDocTypePermission & { _rowKey: string };
type PageAccessRow = PageAccessSetting & { _dirty?: boolean };
type EffectiveAccessTableRow = { table: string; actions: string[] };
type EffectiveAccessRestrictionRow = { restriction: string; values: unknown[] };

const { t } = useI18n();
const { isMobileShell } = useMobileShell();
const toast = useToastify();
const auth = useAuthStore();
const am = (key: string, args?: Record<string, unknown>) => t(`alkokh.accessManagement.${key}`, args ?? {});

const activeTab = ref<AccessTab>("users");
const loading = ref(false);
const usersLoading = ref(false);
const profilesLoading = ref(false);
const rolesLoading = ref(false);
const pageAccessLoading = ref(false);
const userPermissionsLoading = ref(false);
const doctypePermissionsLoading = ref(false);
const previewLoading = ref(false);
const permissionSaving = ref(false);
const pageAccessSaving = ref(false);
const roleSaving = ref(false);

const users = ref<FrappeUser[]>([]);
const roleProfiles = ref<FrappeRoleProfile[]>([]);
const roles = ref<FrappeRole[]>([]);
const pageAccessSettings = ref<PageAccessRow[]>([]);
const userPermissions = ref<FrappeUserPermission[]>([]);
const doctypePermissions = ref<TablePermissionRow[]>([]);
const accessPreview = ref<AccessSnapshot | null>(auth.accessSnapshot);
const userPermissionValueLabels = ref<Record<string, string>>({});

const userSearch = ref("");
const profileSearch = ref("");
const roleSearch = ref("");
const pageAccessSearch = ref("");
const userPermissionSearch = ref("");

const roleDialog = ref(false);
const roleForm = ref<CreateRolePayload>({
  role_name: "",
  desk_access: 1,
  disabled: 0,
  home_page: "",
  restrict_to_domain: "",
});

const userDialog = ref(false);
const selectedUser = ref<FrappeUser | null>(null);
const selectedUserRoleProfiles = ref<string[]>([]);
const selectedUserRoles = ref<string[]>([]);

const profileDialog = ref(false);
const profileDialogMode = ref<"add" | "edit">("add");
const editingProfileName = ref("");
const profileName = ref("");
const profileRoles = ref<string[]>([]);

const deleteProfileDialog = ref(false);
const profileToDelete = ref<FrappeRoleProfile | null>(null);

const userPermissionDialog = ref(false);
const userPermissionForm = ref<CreateUserPermissionPayload>({
  user: "",
  allow: "Warehouse",
  for_value: "",
  apply_to_all_doctypes: 1,
});
const allowedValueOption = ref<FrappeDoctypeLookupOption | null>(null);
const allowedTableOption = ref<FrappeDoctypeLookupOption | null>(null);

const ALLOWED_VALUE_LABEL_FIELDS: Record<string, string> = {
  CategoryCareServices: "category_name",
  "Healthcare Practitioner": "practitioner_name",
  Guardian: "full_name",
  Customer: "customer_name",
  Item: "item_name",
  Pet: "pet_name",
  Warehouse: "warehouse_name",
  Company: "company_name",
  CareService: "service_name",
};

const allowedValueLabelField = computed(() =>
  ALLOWED_VALUE_LABEL_FIELDS[userPermissionForm.value.allow ?? ""] ?? "name",
);

const allowedValueSearchFields = computed(() => {
  const labelField = allowedValueLabelField.value;
  return labelField === "name" ? ["name"] : ["name", labelField];
});

function userPermissionValueLabelKey(doctype?: string | null, value?: string | null) {
  return `${String(doctype || "").trim()}::${String(value || "").trim()}`;
}

function displayUserPermissionValue(row?: FrappeUserPermission | null) {
  if (!row)
    return "";

  return userPermissionValueLabels.value[userPermissionValueLabelKey(row.allow, row.for_value)]
    || row.for_value
    || "";
}
const userPermissionToDelete = ref<FrappeUserPermission | null>(null);
const deleteUserPermissionDialog = ref(false);

const permissionManagerTables = ref<PermissionManagerOption[]>([]);
const permissionManagerRoles = ref<PermissionManagerOption[]>([]);
const permissionTypeMap = ref<Record<string, string[]>>({});
const selectedPermissionTable = ref<string | null>("Sales Invoice");
const selectedPermissionRole = ref<string | null>(null);
const addRuleDialog = ref(false);
const resetTableDialog = ref(false);
const newRule = ref({
  role: "",
  permlevel: 0,
});

const dangerousRoles = new Set([
  "Administrator",
  "System Manager",
  "All",
  "Guest",
  "Website User",
  "Script Manager",
  "Report Manager",
]);

const accessStatCards = computed(() => [
  {
    icon: "tabler-users",
    color: "primary",
    label: am("stats.systemUsers"),
    value: users.value.length,
  },
  {
    icon: "tabler-id-badge-2",
    color: "success",
    label: am("stats.roleProfiles"),
    value: roleProfiles.value.length,
  },
  {
    icon: "tabler-shield-check",
    color: "info",
    label: am("stats.roles"),
    value: roles.value.length,
  },
  {
    icon: "tabler-layout-sidebar",
    color: "secondary",
    label: am("stats.pages"),
    value: pageAccessSettings.value.length,
  },
  {
    icon: "tabler-map-pin-lock",
    color: "warning",
    label: am("stats.userPermissions"),
    value: userPermissions.value.length,
  },
]);

const canManageAccess = computed(() => {
  const roles = [
    ...(auth.accessSnapshot?.roles ?? []),
    ...(auth.permissions ?? []),
  ];

  return hasFullAccessRole(roles) || roles.includes("System Manager");
});

const safeRoleOptions = computed(() =>
  roles.value
    .filter(role => !role.disabled)
    .filter(role => canManageAccess.value || !dangerousRoles.has(role.name))
    .map(role => role.name)
    .sort((a, b) => a.localeCompare(b)),
);

const selectedPermissionTableName = computed(() => selectedPermissionTable.value?.trim() ?? "");
const isRoleWidePermissionView = computed(() =>
  Boolean(!selectedPermissionTableName.value && selectedPermissionRole.value),
);

const userOptions = computed(() =>
  users.value
    .map(user => ({
      title: getUserLabel(user),
      value: user.name,
    }))
    .sort((a, b) => a.title.localeCompare(b.title)),
);

const roleProfileOptions = computed(() =>
  roleProfiles.value
    .map(profile => profile.role_profile || profile.name)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b)),
);

const selectedUserRolePreview = computed(() => {
  const selectedProfileNames = new Set(selectedUserRoleProfiles.value);
  const previewRoles = new Set(selectedUserRoles.value);

  roleProfiles.value.forEach((profile) => {
    const profileLabel = profile.role_profile || profile.name;
    if (!selectedProfileNames.has(profileLabel) && !selectedProfileNames.has(profile.name))
      return;

    getProfileRoles(profile).forEach(role => previewRoles.add(role));
  });

  return [...previewRoles].sort((a, b) => a.localeCompare(b));
});

const filteredUsers = computed(() => {
  const query = userSearch.value.trim().toLowerCase();
  if (!query)
    return users.value;

  return users.value.filter((user) => {
    const haystack = [
      user.name,
      user.email,
      user.full_name,
      user.username,
      ...(user.role_profiles ?? []).map(row => row.role_profile),
      ...(user.roles ?? []).map(row => row.role),
    ].join(" ").toLowerCase();

    return haystack.includes(query);
  });
});

const filteredRoleProfiles = computed(() => {
  const query = profileSearch.value.trim().toLowerCase();
  if (!query)
    return roleProfiles.value;

  return roleProfiles.value.filter((profile) => {
    const haystack = [
      profile.name,
      profile.role_profile,
      ...(profile.roles ?? []).map(row => row.role),
    ].join(" ").toLowerCase();

    return haystack.includes(query);
  });
});

const filteredRoles = computed(() => {
  const query = roleSearch.value.trim().toLowerCase();
  if (!query)
    return roles.value;

  return roles.value.filter((role) => {
    const haystack = [
      role.name,
      role.home_page,
      role.restrict_to_domain,
      isDangerousRole(role.name) ? "sensitive dangerous system" : "",
    ].join(" ").toLowerCase();

    return haystack.includes(query);
  });
});

const filteredPageAccessSettings = computed(() => {
  const query = pageAccessSearch.value.trim().toLowerCase();
  if (!query)
    return pageAccessSettings.value;

  return pageAccessSettings.value.filter((row) => {
    const haystack = [
      row.page_key,
      row.label,
      row.module_key,
      row.doctype,
      ...(row.paths ?? []),
      ...(row.path_prefixes ?? []),
      ...(row.route_names ?? []),
      ...(row.roles ?? []),
    ].join(" ").toLowerCase();

    return haystack.includes(query);
  });
});

const filteredUserPermissions = computed(() => {
  const query = userPermissionSearch.value.trim().toLowerCase();
  if (!query)
    return userPermissions.value;

  return userPermissions.value.filter((row) => {
    const haystack = [
      row.name,
      row.user,
      row.allow,
      row.for_value,
      displayUserPermissionValue(row),
      row.applicable_for,
    ].join(" ").toLowerCase();

    return haystack.includes(query);
  });
});

const regularRoles = computed(() => filteredRoles.value.filter(role => !isDangerousRole(role.name)));
const sensitiveRoles = computed(() => filteredRoles.value.filter(role => isDangerousRole(role.name)));

const roleProfileHeaders = computed(() => [
  { title: am("labels.accessProfile"), key: "role_profile" },
  { title: am("labels.roles"), key: "roles", sortable: false },
  { title: am("labels.modified"), key: "modified" },
  { title: am("labels.actions"), key: "actions", sortable: false, align: "end" as const },
]);

const userHeaders = computed(() => [
  { title: am("labels.user"), key: "user" },
  { title: am("labels.roleProfiles"), key: "role_profiles", sortable: false },
  { title: am("labels.resolvedRoles"), key: "roles", sortable: false },
  { title: am("labels.status"), key: "enabled" },
  { title: am("labels.actions"), key: "actions", sortable: false, align: "end" as const },
]);

const userPermissionHeaders = computed(() => [
  { title: am("labels.user"), key: "user" },
  { title: am("labels.allow"), key: "allow" },
  { title: am("labels.value"), key: "for_value" },
  { title: am("labels.scope"), key: "scope", sortable: false },
  { title: am("labels.actions"), key: "actions", sortable: false, align: "end" as const },
]);

const roleHeaders = computed(() => [
  { title: am("labels.role"), key: "name" },
  { title: am("labels.desk"), key: "desk_access" },
  { title: am("labels.domain"), key: "restrict_to_domain" },
  { title: am("labels.status"), key: "status", sortable: false },
]);

const effectiveAccessTableHeaders = computed(() => [
  { title: am("labels.effectiveTable"), key: "table" },
  { title: am("labels.effectiveActions"), key: "actions", sortable: false },
]);

const effectiveAccessRestrictionHeaders = computed(() => [
  { title: am("labels.restriction"), key: "restriction" },
  { title: am("labels.values"), key: "values", sortable: false },
]);

/**
 * Frontend-registered pages the backend has never heard of. `canPage` refuses any key
 * absent from a backend-sourced snapshot, so a registered-but-unsynced page is blocked for
 * everyone except a full-access bootstrap user — and nothing else on this screen says so.
 * (This is exactly how the Drivers module shipped invisible.)
 */
const unsyncedPages = computed(() => {
  const stored = new Set(pageAccessSettings.value.map(row => row.page_key));

  return permissionPages.filter(page => !stored.has(page.key));
});

const pageAccessHeaders = computed(() => [
  { title: am("labels.page"), key: "page" },
  { title: am("labels.module"), key: "module_key" },
  { title: am("labels.paths"), key: "paths", sortable: false },
  { title: am("labels.status"), key: "enabled", sortable: false },
  { title: am("labels.roles"), key: "roles", sortable: false },
]);

const permissionTypeLabels = computed<Record<PermissionManagerPermissionKey, string>>(() => ({
  select: am("permissionTypes.select"),
  read: am("permissionTypes.read"),
  write: am("permissionTypes.write"),
  create: am("permissionTypes.create"),
  delete: am("permissionTypes.delete"),
  submit: am("permissionTypes.submit"),
  cancel: am("permissionTypes.cancel"),
  amend: am("permissionTypes.amend"),
  report: am("permissionTypes.report"),
  import: am("permissionTypes.import"),
  export: am("permissionTypes.export"),
  print: am("permissionTypes.print"),
  email: am("permissionTypes.email"),
  share: am("permissionTypes.share"),
  if_owner: am("permissionTypes.if_owner"),
}));

const defaultPermissionTypes: PermissionManagerPermissionKey[] = [
  "select",
  "read",
  "write",
  "create",
  "delete",
  "submit",
  "cancel",
  "amend",
  "report",
  "import",
  "export",
  "print",
  "email",
  "share",
  "if_owner",
];

const visiblePermissionTypes = computed<PermissionManagerPermissionKey[]>(() => {
  const configured = permissionTypeMap.value[selectedPermissionTableName.value] ?? [];
  const normalized = configured.filter((item): item is PermissionManagerPermissionKey =>
    defaultPermissionTypes.includes(item as PermissionManagerPermissionKey),
  );

  return normalized.length ? normalized : defaultPermissionTypes;
});

const doctypePermissionHeaders = computed(() => [
  ...(isRoleWidePermissionView.value
    ? [{ title: am("labels.table"), key: "parent" }]
    : []),
  { title: am("labels.role"), key: "role" },
  { title: am("labels.permissionLevel"), key: "permlevel" },
  ...visiblePermissionTypes.value.map(permission => ({
    title: permissionTypeLabels.value[permission],
    key: permission,
    sortable: false,
  })),
  { title: "Actions", key: "actions", sortable: false, align: "end" as const },
]);

const effectiveAccessDoctypes = computed(() =>
  Object.entries(accessPreview.value?.doctypes ?? {})
    .sort(([a], [b]) => a.localeCompare(b)),
);

const effectiveAccessRestrictions = computed(() =>
  Object.entries(accessPreview.value?.restrictions ?? {})
    .sort(([a], [b]) => a.localeCompare(b)),
);

const effectiveAccessTableRows = computed<EffectiveAccessTableRow[]>(() =>
  effectiveAccessDoctypes.value.map(([table, actions]) => ({
    table,
    actions: Array.isArray(actions) ? actions : [],
  })),
);

const effectiveAccessRestrictionRows = computed<EffectiveAccessRestrictionRow[]>(() =>
  effectiveAccessRestrictions.value.map(([restriction, values]) => ({
    restriction,
    values: Array.isArray(values) ? values : [values].filter(value => value != null),
  })),
);

function getUserLabel(user: FrappeUser) {
  return user.full_name || user.email || user.name;
}

function getProfileRoles(profile: FrappeRoleProfile) {
  return (profile.roles ?? [])
    .map(row => row.role)
    .filter(Boolean);
}

function getUserRoleProfiles(user: FrappeUser) {
  return (user.role_profiles ?? [])
    .map(row => row.role_profile)
    .filter(Boolean);
}

function getUserRoles(user: FrappeUser) {
  return (user.roles ?? [])
    .map(row => row.role)
    .filter(Boolean);
}

function isDangerousRole(role: string) {
  return dangerousRoles.has(role);
}

function getTablePermissionRowKey(row: FrappeDocTypePermission, index: number) {
  return [
    row.name || "permission-rule",
    row.parent || selectedPermissionTableName.value || "",
    row.role || "",
    Number(row.permlevel ?? 0),
    Number(row.if_owner ?? 0),
    index,
  ].join("::");
}

function normalizeTablePermissionRows(rows: FrappeDocTypePermission[]): TablePermissionRow[] {
  return rows.map((row, index) => ({
    ...row,
    _rowKey: getTablePermissionRowKey(row, index),
  }));
}

function patchLocalTablePermission(
  rowKey: string,
  permission: PermissionManagerPermissionKey,
  value: 0 | 1,
) {
  doctypePermissions.value = doctypePermissions.value.map((row) => {
    if (row._rowKey !== rowKey)
      return row;

    return {
      ...row,
      [permission]: value,
    };
  });
}

async function withLoading<T>(flag: Ref<boolean>, task: () => Promise<T>) {
  flag.value = true;
  loading.value = true;

  try {
    return await task();
  } finally {
    flag.value = false;
    loading.value = [
      usersLoading,
      profilesLoading,
      rolesLoading,
      pageAccessLoading,
      userPermissionsLoading,
      doctypePermissionsLoading,
      previewLoading,
    ].some(item => item.value);
  }
}

function accessErrorMessage(error: any, fallback: string) {
  const data = error?.response?.data;
  const serverMessages = data?._server_messages;

  if (serverMessages) {
    try {
      const parsed = JSON.parse(serverMessages);
      const message = Array.isArray(parsed) ? parsed[0] : parsed;
      let parsedMessage = message;

      if (typeof message === "string") {
        try {
          parsedMessage = JSON.parse(message);
        } catch {
          return message;
        }
      }

      if (parsedMessage?.message)
        return parsedMessage.message;
    } catch {
      return String(serverMessages);
    }
  }

  return String(data?.message || data?.exception || error?.message || fallback);
}

async function loadUsers() {
  await withLoading(usersLoading, async () => {
    users.value = await GetUsers();
  });
}

async function loadRoleProfiles() {
  await withLoading(profilesLoading, async () => {
    roleProfiles.value = await GetRoleProfiles();
  });
}

async function loadRoles() {
  await withLoading(rolesLoading, async () => {
    roles.value = await GetRoles();
  });
}

function openCreateRoleDialog() {
  roleForm.value = {
    role_name: "",
    desk_access: 1,
    disabled: 0,
    home_page: "",
    restrict_to_domain: "",
  };
  roleDialog.value = true;
}

function closeRoleDialog() {
  roleDialog.value = false;
  roleForm.value = {
    role_name: "",
    desk_access: 1,
    disabled: 0,
    home_page: "",
    restrict_to_domain: "",
  };
}

async function saveRole() {
  const roleName = roleForm.value.role_name.trim();
  if (!roleName) {
    toast.error({ text: am("messages.roleNameRequired") });

    return;
  }

  roleSaving.value = true;

  try {
    await CreateRole({
      role_name: roleName,
      desk_access: Number(roleForm.value.desk_access ?? 0) === 1 ? 1 : 0,
      disabled: Number(roleForm.value.disabled ?? 0) === 1 ? 1 : 0,
      home_page: roleForm.value.home_page?.trim() || null,
      restrict_to_domain: roleForm.value.restrict_to_domain?.trim() || null,
    });

    toast.success({ text: am("messages.roleCreated") });
    closeRoleDialog();
    await Promise.all([
      loadRoles(),
      loadPermissionManagerOptions(),
    ]);
  } catch (error: any) {
    toast.error({ text: accessErrorMessage(error, am("messages.roleCreateFailed")) });
  } finally {
    roleSaving.value = false;
  }
}

// Sync ships PAGE IDENTITY ONLY — never roles.
//
// `fallbackRoles` used to be sent here, and the backend seeded a newly created
// page row's `roles_json` from it. That made the frontend registry an implicit
// author of backend permissions: every sync of a not-yet-stored page silently
// granted it to a broad hardcoded role list (e.g. HEALTHCARE_ROLES), which is
// not a decision this app is entitled to make. Role assignment belongs to the
// admin, through `update_page_access_settings` (the Save action below).
//
// The registry's `fallbackRoles` still exist and are still used — but strictly
// LOCALLY, and only when the snapshot is `roles-fallback` (see
// helper/permissionHelper.ts). They must not cross the wire.
function buildFrontendPageCatalog(): FrontendPageAccessPayload[] {
  return permissionPages.map(page => ({
    key: page.key,
    label: t(page.label),
    moduleKey: page.moduleKey,
    doctype: page.doctype,
    paths: page.paths ?? [],
    pathPrefixes: page.pathPrefixes ?? [],
    routeNames: page.routeNames ?? [],
  }));
}

function normalizePageAccessRows(rows: PageAccessSetting[]): PageAccessRow[] {
  return rows.map(row => ({
    ...row,
    enabled: Number(row.enabled ?? 0) === 1 ? 1 : 0,
    roles: [...(row.roles ?? [])],
    paths: [...(row.paths ?? [])],
    path_prefixes: [...(row.path_prefixes ?? [])],
    route_names: [...(row.route_names ?? [])],
    _dirty: false,
  }));
}

function markPageAccessDirty(row: PageAccessRow) {
  row._dirty = true;
}

function getPageAccessPaths(row: PageAccessRow) {
  return [
    ...(row.paths ?? []),
    ...(row.path_prefixes ?? []),
  ].filter(Boolean);
}

async function loadPageAccessSettings() {
  await withLoading(pageAccessLoading, async () => {
    pageAccessSettings.value = normalizePageAccessRows(await GetPageAccessSettings());
  });
}

async function syncFrontendPages() {
  if (!canManageAccess.value)
    return;

  pageAccessSaving.value = true;

  try {
    pageAccessSettings.value = normalizePageAccessRows(
      await SyncFrontendPages(buildFrontendPageCatalog()),
    );
    toast.success({ text: am("messages.pagesSynced") });
  } catch (error: any) {
    toast.error({ text: error?.message || am("messages.pagesSyncFailed") });
  } finally {
    pageAccessSaving.value = false;
  }
}

async function savePageAccessSettings() {
  if (!canManageAccess.value)
    return;

  const dirtyRows = pageAccessSettings.value.filter(row => row._dirty);
  if (!dirtyRows.length) {
    toast.success({ text: am("messages.noPageAccessChanges") });

    return;
  }

  pageAccessSaving.value = true;

  try {
    pageAccessSettings.value = normalizePageAccessRows(
      await UpdatePageAccessSettings(dirtyRows.map(row => ({
        page_key: row.page_key,
        enabled: Number(row.enabled ?? 0) === 1 ? 1 : 0,
        roles: row.roles ?? [],
      }))),
    );
    toast.success({ text: am("messages.pageAccessSaved") });
    await refreshEffectiveAccess();
  } catch (error: any) {
    toast.error({ text: error?.message || am("messages.pageAccessSaveFailed") });
  } finally {
    pageAccessSaving.value = false;
  }
}

async function loadUserPermissions() {
  await withLoading(userPermissionsLoading, async () => {
    userPermissions.value = await GetUserPermissions();
    await resolveUserPermissionValueLabels(userPermissions.value);
  });
}

async function resolveUserPermissionValueLabels(rows: FrappeUserPermission[]) {
  const grouped = new Map<string, Set<string>>();

  rows.forEach((row) => {
    const doctype = String(row.allow || "").trim();
    const value = String(row.for_value || "").trim();
    const labelField = ALLOWED_VALUE_LABEL_FIELDS[doctype];

    if (!doctype || !value || !labelField || labelField === "name")
      return;

    if (!grouped.has(doctype))
      grouped.set(doctype, new Set());

    grouped.get(doctype)!.add(value);
  });

  if (!grouped.size)
    return;

  const nextLabels = { ...userPermissionValueLabels.value };

  await Promise.all([...grouped.entries()].map(async ([doctype, values]) => {
    const ids = [...values];
    const labelField = ALLOWED_VALUE_LABEL_FIELDS[doctype];

    try {
      const result = await searchFrappeDoctype(ref(false), {
        doctype,
        fields: [labelField],
        labelField,
        searchFields: ["name", labelField],
        baseFilters: [[doctype, "name", "in", ids]],
        limit: Math.max(ids.length, 1),
        orderBy: "name asc",
      });

      result.items.forEach((item) => {
        nextLabels[userPermissionValueLabelKey(doctype, item.id)] = item.label;
      });
    } catch (error) {
      console.warn(`Failed to resolve ${doctype} permission labels`, error);
    }
  }));

  userPermissionValueLabels.value = nextLabels;
}

async function loadPermissionManagerOptions() {
  const options = await GetPermissionManagerOptions();

  permissionManagerTables.value = options.tables;
  permissionManagerRoles.value = options.roles;
  permissionTypeMap.value = options.permissionTypeMap;
}

async function getPermissionRowsForSelection() {
  const table = selectedPermissionTableName.value;
  const role = selectedPermissionRole.value;

  if (!table && !role)
    return [];

  if (table)
    return GetPermissionManagerPermissions(table, role);

  const permissionRows = await Promise.all(
    permissionManagerTables.value.map(async (option) => {
      const tableName = option.value;
      const rows = await GetPermissionManagerPermissions(tableName, role);

      return rows.map(row => ({
        ...row,
        parent: row.parent || tableName,
      }));
    }),
  );

  return permissionRows.flat();
}

async function loadTablePermissions() {
  await withLoading(doctypePermissionsLoading, async () => {
    doctypePermissions.value = normalizeTablePermissionRows(await getPermissionRowsForSelection());
  });
}

async function refreshEffectiveAccess() {
  await withLoading(previewLoading, async () => {
    const snapshot = await GetCurrentAccess();
    accessPreview.value = snapshot;

    if (snapshot)
      auth.setAccessSnapshot(snapshot);
  });
}

async function refreshAll() {
  await Promise.all([
    loadUsers(),
    loadRoleProfiles(),
    loadRoles(),
    loadPageAccessSettings(),
    loadUserPermissions(),
    withLoading(doctypePermissionsLoading, async () => {
      await loadPermissionManagerOptions();
      doctypePermissions.value = normalizeTablePermissionRows(await getPermissionRowsForSelection());
    }),
    refreshEffectiveAccess(),
  ]);
}

function openUserDialog(user: FrappeUser) {
  selectedUser.value = user;
  selectedUserRoleProfiles.value = getUserRoleProfiles(user);
  selectedUserRoles.value = getUserRoles(user);
  userDialog.value = true;
}

function closeUserDialog() {
  userDialog.value = false;
  selectedUser.value = null;
  selectedUserRoleProfiles.value = [];
  selectedUserRoles.value = [];
}

async function saveUserRoleAssignments() {
  if (!selectedUser.value)
    return;

  await UpdateUserRoleAssignments(
    selectedUser.value.name,
    selectedUserRoleProfiles.value.map(roleProfile => ({ role_profile: roleProfile })),
    selectedUserRoles.value.map(role => ({ role })),
  );

  toast.success({ text: am("messages.profilesAssigned") });
  closeUserDialog();
  await loadUsers();
}

function openCreateProfileDialog() {
  profileDialogMode.value = "add";
  editingProfileName.value = "";
  profileName.value = "";
  profileRoles.value = [];
  profileDialog.value = true;
}

function openEditProfileDialog(profile: FrappeRoleProfile) {
  profileDialogMode.value = "edit";
  editingProfileName.value = profile.name;
  profileName.value = profile.role_profile || profile.name;
  profileRoles.value = getProfileRoles(profile);
  profileDialog.value = true;
}

function closeProfileDialog() {
  profileDialog.value = false;
  editingProfileName.value = "";
  profileName.value = "";
  profileRoles.value = [];
}

async function saveRoleProfile() {
  const payload = {
    role_profile: profileName.value.trim(),
    roles: profileRoles.value.map(role => ({ role })),
    // Flag profiles managed from this app so the backend can distinguish frontend-created profiles.
    custom_is_front: 1 as const,
  };

  if (!payload.role_profile) {
    toast.error({ text: am("messages.profileNameRequired") });

    return;
  }

  if (profileDialogMode.value === "edit")
    await UpdateRoleProfile(editingProfileName.value, payload);
  else
    await CreateRoleProfile(payload);

  toast.success({ text: profileDialogMode.value === "edit" ? am("messages.profileUpdated") : am("messages.profileCreated") });
  closeProfileDialog();
  await loadRoleProfiles();
}

function openDeleteProfileDialog(profile: FrappeRoleProfile) {
  profileToDelete.value = profile;
  deleteProfileDialog.value = true;
}

async function confirmDeleteProfile() {
  if (!profileToDelete.value)
    return;

  await DeleteRoleProfile(profileToDelete.value.name);
  toast.success({ text: am("messages.profileDeleted") });
  deleteProfileDialog.value = false;
  profileToDelete.value = null;
  await loadRoleProfiles();
}

function openUserPermissionDialog() {
  userPermissionForm.value = {
    user: "",
    allow: "Warehouse",
    for_value: "",
    apply_to_all_doctypes: 1,
  };
  allowedValueOption.value = null;
  allowedTableOption.value = {
    id: "Warehouse",
    label: "Warehouse",
    subtitle: null,
    image: null,
    raw: { name: "Warehouse" },
  };
  userPermissionDialog.value = true;
}

async function saveUserPermission() {
  userPermissionForm.value.for_value = allowedValueOption.value?.id || userPermissionForm.value.for_value;

  const payload = {
    ...userPermissionForm.value,
    user: userPermissionForm.value.user.trim(),
    allow: userPermissionForm.value.allow.trim(),
    for_value: userPermissionForm.value.for_value.trim(),
  };

  if (!payload.user || !payload.allow || !payload.for_value) {
    toast.error({ text: am("messages.userPermissionRequired") });

    return;
  }

  await CreateUserPermission(payload);
  toast.success({ text: am("messages.userPermissionAdded") });
  userPermissionDialog.value = false;
  await loadUserPermissions();
}

function hasPermissionFlag(row: FrappeDocTypePermission, key: keyof FrappeDocTypePermission) {
  return Number(row[key] ?? 0) === 1;
}

async function toggleTablePermission(
  row: TablePermissionRow,
  permission: PermissionManagerPermissionKey,
  value: boolean,
) {
  const table = row.parent || selectedPermissionTableName.value;

  if (!table || !row.role)
    return;

  permissionSaving.value = true;
  const previousValue = hasPermissionFlag(row, permission) ? 1 : 0;

  try {
    const numericValue = value ? 1 : 0;
    const ifOwner = Number(row.if_owner ?? 0) === 1 ? 1 : 0;

    patchLocalTablePermission(row._rowKey, permission, numericValue);

    await UpdatePermissionManagerRule(
      table,
      row.role,
      Number(row.permlevel ?? 0),
      permission,
      numericValue,
      ifOwner,
    );

    await loadTablePermissions();
    toast.success({ text: t("alkokh.accessManagement.messages.tablePermissionUpdated") });
  } catch (error: any) {
    patchLocalTablePermission(row._rowKey, permission, previousValue);
    toast.error({ text: error?.message || t("alkokh.accessManagement.messages.tablePermissionUpdateFailed") });
  } finally {
    permissionSaving.value = false;
  }
}

function openAddRuleDialog() {
  newRule.value = {
    role: selectedPermissionRole.value || "",
    permlevel: 0,
  };
  addRuleDialog.value = true;
}

function openResetTableDialog() {
  if (!selectedPermissionTableName.value)
    return;

  resetTableDialog.value = true;
}

async function addTablePermissionRule() {
  if (!selectedPermissionTableName.value || !newRule.value.role) {
    toast.error({ text: t("alkokh.accessManagement.messages.roleRequired") });

    return;
  }

  permissionSaving.value = true;

  try {
    await AddPermissionManagerRule(selectedPermissionTableName.value, newRule.value.role, Number(newRule.value.permlevel ?? 0));
    toast.success({ text: t("alkokh.accessManagement.messages.tablePermissionAdded") });
    addRuleDialog.value = false;
    await loadTablePermissions();
  } catch (error: any) {
    toast.error({ text: error?.message || t("alkokh.accessManagement.messages.tablePermissionAddFailed") });
  } finally {
    permissionSaving.value = false;
  }
}

async function removeTablePermissionRule(row: FrappeDocTypePermission) {
  const table = row.parent || selectedPermissionTableName.value;

  if (!table || !row.role)
    return;

  permissionSaving.value = true;

  try {
    await RemovePermissionManagerRule(
      table,
      row.role,
      Number(row.permlevel ?? 0),
      Number(row.if_owner ?? 0) === 1 ? 1 : 0,
    );
    toast.success({ text: t("alkokh.accessManagement.messages.tablePermissionRemoved") });
    await loadTablePermissions();
  } catch (error: any) {
    toast.error({ text: error?.message || t("alkokh.accessManagement.messages.tablePermissionRemoveFailed") });
  } finally {
    permissionSaving.value = false;
  }
}

async function resetTablePermissions() {
  if (!selectedPermissionTableName.value)
    return;

  permissionSaving.value = true;

  try {
    await ResetPermissionManagerRules(selectedPermissionTableName.value);
    toast.success({ text: t("alkokh.accessManagement.messages.tablePermissionsReset") });
    resetTableDialog.value = false;
    await loadTablePermissions();
  } catch (error: any) {
    toast.error({ text: error?.message || t("alkokh.accessManagement.messages.tablePermissionsResetFailed") });
  } finally {
    permissionSaving.value = false;
  }
}

function openDeleteUserPermissionDialog(row: FrappeUserPermission) {
  userPermissionToDelete.value = row;
  deleteUserPermissionDialog.value = true;
}

async function confirmDeleteUserPermission() {
  if (!userPermissionToDelete.value)
    return;

  await DeleteUserPermission(userPermissionToDelete.value.name);
  toast.success({ text: am("messages.userPermissionRemoved") });
  deleteUserPermissionDialog.value = false;
  userPermissionToDelete.value = null;
  await loadUserPermissions();
}

watch(
  () => userPermissionForm.value.allow,
  () => {
    userPermissionForm.value.for_value = "";
    allowedValueOption.value = null;
  },
);

watch(allowedTableOption, (option) => {
  userPermissionForm.value.allow = option?.id || "";
});

watch([selectedPermissionRole, selectedPermissionTable], () => {
  loadTablePermissions();
});

onMounted(refreshAll);
</script>

<template>
  <section class="access-management">
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <div class="d-flex align-center gap-3 mb-2">
          <VAvatar color="primary" variant="tonal" rounded size="44">
            <VIcon icon="tabler-shield-lock" size="26" />
          </VAvatar>
          <div>
            <h1 class="text-h4 mb-1">
              {{ am("title") }}
            </h1>
            <p class="text-body-1 text-medium-emphasis mb-0">
              {{ am("subtitle") }}
            </p>
          </div>
        </div>
      </div>

      <VBtn
        color="primary"
        prepend-icon="tabler-refresh"
        :loading="loading"
        @click="refreshAll"
      >
        {{ am("refresh") }}
      </VBtn>
    </div>

    <VRow class="mb-6">
      <VCol
        v-for="card in accessStatCards"
        :key="card.label"
        cols="12"
        sm="6"
        lg="3"
      >
        <VCard class="access-stat-card" variant="tonal" :color="card.color">
          <VCardText class="d-flex align-center gap-4">
            <VAvatar :color="card.color" variant="flat" rounded size="46">
              <VIcon :icon="card.icon" size="25" />
            </VAvatar>
            <div>
              <div class="text-h5 font-weight-semibold">
                {{ card.value }}
              </div>
              <div class="text-body-2">
                {{ card.label }}
              </div>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VAlert
      v-if="!canManageAccess"
      type="info"
      variant="tonal"
      class="mb-6"
    >
      {{ am("messages.manageReadOnly") }}
    </VAlert>

    <VTabs
      v-model="activeTab"
      class="mb-4"
      density="comfortable"
    >
      <VTab value="users">
        <VIcon icon="tabler-users" start />
        {{ am("tabs.users") }}
      </VTab>
      <VTab value="role-profiles">
        <VIcon icon="tabler-id-badge-2" start />
        {{ am("tabs.roleProfiles") }}
      </VTab>
      <VTab value="roles">
        <VIcon icon="tabler-shield" start />
        {{ am("tabs.roles") }}
      </VTab>
      <VTab value="page-access">
        <VIcon icon="tabler-layout-sidebar" start />
        {{ am("tabs.pageAccess") }}
      </VTab>
      <VTab value="role-permissions">
        <VIcon icon="tabler-table-options" start />
        {{ am("tabs.tablePermissions") }}
      </VTab>
      <VTab value="user-permissions">
        <VIcon icon="tabler-lock-access" start />
        {{ am("tabs.userPermissions") }}
      </VTab>
      <VTab value="effective-access">
        <VIcon icon="tabler-eye-check" start />
        {{ am("tabs.effectiveAccess") }}
      </VTab>
    </VTabs>

    <VWindow v-model="activeTab">
      <VWindowItem value="users">
        <VCard>
          <VCardText class="d-flex flex-wrap align-center justify-space-between gap-4">
            <VTextField
              v-model="userSearch"
              prepend-inner-icon="tabler-search"
              :label="am('search.users')"
              density="compact"
              clearable
              class="access-search"
            />
            <VChip color="primary" variant="tonal">
              {{ filteredUsers.length }} users
            </VChip>
          </VCardText>
          <VDivider />
          <VDataTable
            :headers="userHeaders"
            :items="filteredUsers"
            :loading="usersLoading"
            item-value="name"
            class="text-no-wrap"
          >
            <template #item.user="{ item }">
              <div class="d-flex align-center gap-3">
                <VAvatar size="36" color="secondary" variant="tonal">
                  <VImg v-if="item.user_image" :src="item.user_image" />
                  <span v-else>{{ getUserLabel(item).charAt(0).toUpperCase() }}</span>
                </VAvatar>
                <div>
                  <div class="font-weight-medium">
                    {{ getUserLabel(item) }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ item.email || item.name }}
                  </div>
                </div>
              </div>
            </template>

            <template #item.role_profiles="{ item }">
              <div class="d-flex flex-wrap gap-2">
                <VChip
                  v-for="profile in getUserRoleProfiles(item)"
                  :key="profile"
                  size="small"
                  color="primary"
                  variant="tonal"
                >
                  {{ profile }}
                </VChip>
                <span v-if="!getUserRoleProfiles(item).length" class="text-medium-emphasis">
                  No role profile
                </span>
              </div>
            </template>

            <template #item.roles="{ item }">
              <div class="d-flex flex-wrap gap-2 access-chip-stack">
                <VChip
                  v-for="role in getUserRoles(item)"
                  :key="role"
                  size="small"
                  :color="isDangerousRole(role) ? 'error' : 'secondary'"
                  variant="tonal"
                >
                  {{ role }}
                </VChip>
                <span v-if="!getUserRoles(item).length" class="text-medium-emphasis">
                  No resolved roles
                </span>
              </div>
            </template>

            <template #item.enabled="{ item }">
              <VChip
                size="small"
                :color="item.enabled ? 'success' : 'secondary'"
                variant="tonal"
              >
                  {{ item.enabled ? am("labels.enabled") : am("labels.disabled") }}
              </VChip>
            </template>

            <template #item.actions="{ item }">
              <VBtn
                size="small"
                variant="tonal"
                prepend-icon="tabler-id-badge-2"
                :disabled="!canManageAccess"
                @click="openUserDialog(item)"
              >
                {{ am("actions.assign") }}
              </VBtn>
            </template>
          </VDataTable>
        </VCard>
      </VWindowItem>

      <VWindowItem value="role-profiles">
        <VCard>
          <VCardText class="d-flex flex-wrap align-center justify-space-between gap-4">
            <VTextField
              v-model="profileSearch"
              prepend-inner-icon="tabler-search"
              :label="am('search.profiles')"
              density="compact"
              clearable
              class="access-search"
            />
            <VBtn
              color="primary"
              prepend-icon="tabler-plus"
              :disabled="!canManageAccess"
              @click="openCreateProfileDialog"
            >
              {{ am("actions.newRoleProfile") }}
            </VBtn>
          </VCardText>
          <VDivider />
          <VDataTable
            :headers="roleProfileHeaders"
            :items="filteredRoleProfiles"
            :loading="profilesLoading"
            item-value="name"
          >
            <template #item.role_profile="{ item }">
              <div>
                <div class="font-weight-medium">
                  {{ item.role_profile || item.name }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ item.name }}
                </div>
              </div>
            </template>

            <template #item.roles="{ item }">
              <div class="d-flex flex-wrap gap-2 access-chip-stack">
                <VChip
                  v-for="role in getProfileRoles(item)"
                  :key="role"
                  size="small"
                  :color="isDangerousRole(role) ? 'error' : 'secondary'"
                  variant="tonal"
                >
                  {{ role }}
                </VChip>
                <span v-if="!getProfileRoles(item).length" class="text-medium-emphasis">
                  No roles
                </span>
              </div>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex justify-end gap-2">
                <IconBtn
                  :disabled="!canManageAccess"
                  @click="openEditProfileDialog(item)"
                >
                  <VIcon icon="tabler-edit" />
                  <VTooltip activator="parent">
                    Edit role profile
                  </VTooltip>
                </IconBtn>
                <IconBtn
                  color="error"
                  :disabled="!canManageAccess"
                  @click="openDeleteProfileDialog(item)"
                >
                  <VIcon icon="tabler-trash" />
                  <VTooltip activator="parent">
                    Delete role profile
                  </VTooltip>
                </IconBtn>
              </div>
            </template>
          </VDataTable>
        </VCard>
      </VWindowItem>

      <VWindowItem value="roles">
        <VRow>
          <VCol cols="12">
            <VCard>
              <VCardText class="d-flex flex-wrap align-center justify-space-between gap-4">
                <VTextField
              v-model="roleSearch"
              prepend-inner-icon="tabler-search"
              :label="am('search.roles')"
                  density="compact"
                  clearable
                  class="access-search"
                />
                <VBtn
                  color="primary"
                  prepend-icon="tabler-plus"
                  :disabled="!canManageAccess"
                  @click="openCreateRoleDialog"
                >
                  {{ am("actions.newRole") }}
                </VBtn>
              </VCardText>
              <VDivider />
              <VDataTable
                :headers="roleHeaders"
                :items="regularRoles"
                :loading="rolesLoading"
                item-value="name"
              >
                <template #item.desk_access="{ item }">
                  <VChip
                    size="small"
                    :color="item.desk_access ? 'success' : 'secondary'"
                    variant="tonal"
                  >
                  {{ item.desk_access ? am("labels.desk") : am("labels.noDesk") }}
                  </VChip>
                </template>
                <template #item.restrict_to_domain="{ item }">
                  {{ item.restrict_to_domain || "-" }}
                </template>
                <template #item.status="{ item }">
                  <VChip
                    size="small"
                    :color="item.disabled ? 'secondary' : 'success'"
                    variant="tonal"
                  >
                    {{ item.disabled ? am("labels.disabled") : am("labels.active") }}
                  </VChip>
                </template>
              </VDataTable>
            </VCard>
          </VCol>

          <VCol v-if="sensitiveRoles.length" cols="12">
            <VCard>
              <VCardTitle class="d-flex align-center gap-2">
                <VIcon icon="tabler-alert-triangle" color="warning" />
                Sensitive Roles
              </VCardTitle>
              <VCardText class="pt-0">
                <div class="d-flex flex-wrap gap-2">
                  <VChip
                    v-for="role in sensitiveRoles"
                    :key="role.name"
                    color="warning"
                    variant="tonal"
                  >
                    {{ role.name }}
                  </VChip>
                </div>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VWindowItem>

      <VWindowItem value="page-access">
        <VCard>
          <VCardText>
            <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-5">
              <div>
                <h2 class="text-h6 mb-1">
                  {{ am("tabs.pageAccess") }}
                </h2>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  {{ am("pageAccessHelp") }}
                </p>
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VBtn
                  color="primary"
                  prepend-icon="tabler-refresh"
                  :loading="pageAccessSaving"
                  :disabled="!canManageAccess"
                  @click="syncFrontendPages"
                >
                  {{ am("actions.syncPages") }}
                </VBtn>
                <VBtn
                  color="success"
                  variant="tonal"
                  prepend-icon="tabler-device-floppy"
                  :loading="pageAccessSaving"
                  :disabled="!canManageAccess || !pageAccessSettings.some(row => row._dirty)"
                  @click="savePageAccessSettings"
                >
                  {{ am("actions.savePageAccess") }}
                </VBtn>
              </div>
            </div>

            <VAlert
              v-if="!pageAccessLoading && unsyncedPages.length"
              type="warning"
              variant="tonal"
              density="comfortable"
              class="mb-5"
              :title="am('pageAccessUnsyncedTitle')"
              :text="am('pageAccessUnsynced', {
                count: unsyncedPages.length,
                pages: unsyncedPages.map(page => t(page.label)).join(', '),
              })"
            />

            <VTextField
              v-model="pageAccessSearch"
              prepend-inner-icon="tabler-search"
              :label="am('search.pageAccess')"
              density="compact"
              clearable
              class="access-search"
            />
          </VCardText>
          <VDivider />
          <VDataTable
            :headers="pageAccessHeaders"
            :items="filteredPageAccessSettings"
            :loading="pageAccessLoading || pageAccessSaving"
            item-value="page_key"
            class="text-no-wrap"
          >
            <template #item.page="{ item }">
              <div>
                <div class="font-weight-medium">
                  {{ item.label || item.page_key }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ item.page_key }}
                </div>
                <VChip
                  v-if="item.doctype"
                  size="x-small"
                  color="secondary"
                  variant="tonal"
                  class="mt-1"
                >
                  {{ item.doctype }}
                </VChip>
              </div>
            </template>

            <template #item.module_key="{ item }">
              <VChip size="small" color="primary" variant="tonal">
                {{ item.module_key || "-" }}
              </VChip>
            </template>

            <template #item.paths="{ item }">
              <div class="d-flex flex-wrap gap-1 access-path-stack">
                <VChip
                  v-for="path in getPageAccessPaths(item)"
                  :key="`${item.page_key}-${path}`"
                  size="x-small"
                  color="secondary"
                  variant="tonal"
                >
                  {{ path }}
                </VChip>
                <span v-if="!getPageAccessPaths(item).length" class="text-medium-emphasis">
                  -
                </span>
              </div>
            </template>

            <template #item.enabled="{ item }">
              <VSwitch
                v-model="item.enabled"
                :true-value="1"
                :false-value="0"
                color="primary"
                density="compact"
                hide-details
                :disabled="!canManageAccess || pageAccessSaving"
                :label="item.enabled ? am('labels.active') : am('labels.disabled')"
                @update:model-value="markPageAccessDirty(item)"
              />
            </template>

            <template #item.roles="{ item }">
              <VSelect
                v-model="item.roles"
                :items="safeRoleOptions"
                multiple
                chips
                closable-chips
                density="compact"
                hide-details
                :disabled="!canManageAccess || pageAccessSaving"
                class="access-role-select"
                @update:model-value="markPageAccessDirty(item)"
              />
            </template>
          </VDataTable>
          <VAlert
            v-if="!pageAccessLoading && !pageAccessSettings.length"
            type="info"
            variant="tonal"
            class="ma-4"
          >
            {{ am("messages.syncPagesEmpty") }}
          </VAlert>
        </VCard>
      </VWindowItem>

      <VWindowItem value="role-permissions">
        <VCard>
          <VCardText>
            <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-5">
              <div>
                <h2 class="text-h6 mb-1">
                  {{ am("tabs.tablePermissions") }}
                </h2>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  {{ t("alkokh.accessManagement.subtitle") }}
                </p>
              </div>
              <div class="d-flex flex-wrap gap-2">
                <VBtn
                  color="primary"
                  prepend-icon="tabler-plus"
                  :disabled="!canManageAccess || !selectedPermissionTableName"
                  @click="openAddRuleDialog"
                >
                  {{ am("actions.addRule") }}
                </VBtn>
                <VBtn
                  color="warning"
                  variant="tonal"
                  prepend-icon="tabler-restore"
                  :loading="permissionSaving"
                  :disabled="!canManageAccess || !selectedPermissionTableName"
                  @click="openResetTableDialog"
                >
                  {{ am("actions.resetTable") }}
                </VBtn>
              </div>
            </div>

            <div class="d-flex flex-wrap align-center gap-3">
              <VAutocomplete
                v-model="selectedPermissionTable"
                :items="permissionManagerTables"
                item-title="label"
                item-value="value"
                :label="am('labels.table')"
                density="compact"
                clearable
                class="access-lookup"
              />
              <VAutocomplete
                v-model="selectedPermissionRole"
                :items="permissionManagerRoles"
                item-title="label"
                item-value="value"
                :label="am('labels.role')"
                density="compact"
                clearable
                class="access-lookup"
              />
              <VBtn
                variant="tonal"
                prepend-icon="tabler-refresh"
                :loading="doctypePermissionsLoading"
                @click="loadTablePermissions"
              >
                {{ am("actions.loadRules") }}
              </VBtn>
            </div>
          </VCardText>
          <VDivider />
          <VDataTable
            :headers="doctypePermissionHeaders"
            :items="doctypePermissions"
            item-value="_rowKey"
            :loading="doctypePermissionsLoading || permissionSaving"
            class="text-no-wrap"
          >
            <template #item.parent="{ item }">
              <VChip color="secondary" variant="tonal" size="small">
                {{ item.parent || selectedPermissionTableName }}
              </VChip>
            </template>

            <template #item.role="{ item }">
              <VChip color="primary" variant="tonal" size="small">
                {{ item.role }}
              </VChip>
            </template>

            <template
              v-for="permission in visiblePermissionTypes"
              :key="permission"
              #[`item.${permission}`]="{ item }"
            >
              <VCheckboxBtn
                :key="`${item._rowKey}-${permission}-${Number(item[permission] ?? 0)}`"
                :model-value="hasPermissionFlag(item, permission)"
                color="primary"
                density="compact"
                :disabled="!canManageAccess || permissionSaving"
                @update:model-value="toggleTablePermission(item, permission, Boolean($event))"
              />
            </template>

            <template #item.actions="{ item }">
              <IconBtn
                color="error"
                :disabled="!canManageAccess || permissionSaving"
                @click="removeTablePermissionRule(item)"
              >
                <VIcon icon="tabler-trash" />
                <VTooltip activator="parent">
                  Remove rule
                </VTooltip>
              </IconBtn>
            </template>
          </VDataTable>
        </VCard>
      </VWindowItem>

      <VWindowItem value="user-permissions">
        <VCard>
          <VCardText class="d-flex flex-wrap align-center justify-space-between gap-4">
            <VTextField
              v-model="userPermissionSearch"
              prepend-inner-icon="tabler-search"
              :label="am('search.userPermissions')"
              density="compact"
              clearable
              class="access-search"
            />
            <VBtn
              color="primary"
              prepend-icon="tabler-plus"
              :disabled="!canManageAccess"
              @click="openUserPermissionDialog"
            >
              {{ am("actions.newUserPermission") }}
            </VBtn>
          </VCardText>
          <VDivider />
          <VDataTable
            :headers="userPermissionHeaders"
            :items="filteredUserPermissions"
            :loading="userPermissionsLoading"
            item-value="name"
          >
            <template #item.for_value="{ item }">
              <div>
                <div class="font-weight-medium">
                  {{ displayUserPermissionValue(item) }}
                </div>
                <div
                  v-if="displayUserPermissionValue(item) !== item.for_value"
                  class="text-caption text-medium-emphasis"
                >
                  {{ item.for_value }}
                </div>
              </div>
            </template>

            <template #item.scope="{ item }">
              <VChip size="small" color="secondary" variant="tonal">
                {{ item.apply_to_all_doctypes ? am("labels.allTables") : item.applicable_for || am("labels.selectedTables") }}
              </VChip>
            </template>

            <template #item.actions="{ item }">
              <IconBtn
                color="error"
                :disabled="!canManageAccess"
                @click="openDeleteUserPermissionDialog(item)"
              >
                <VIcon icon="tabler-trash" />
                <VTooltip activator="parent">
                  Delete user permission
                </VTooltip>
              </IconBtn>
            </template>
          </VDataTable>
        </VCard>
      </VWindowItem>

      <VWindowItem value="effective-access">
        <VCard :loading="previewLoading">
          <VCardText class="d-flex flex-wrap align-center justify-space-between gap-4">
            <div>
              <h2 class="text-h6 mb-1">
                {{ am("tabs.effectiveAccess") }}
              </h2>
              <p class="text-body-2 text-medium-emphasis mb-0">
                What this signed-in user can actually do after roles, table permissions, and user restrictions are resolved.
              </p>
            </div>
            <VBtn
              prepend-icon="tabler-refresh"
              variant="tonal"
              :loading="previewLoading"
              @click="refreshEffectiveAccess"
            >
              {{ am("actions.refreshEffectiveAccess") }}
            </VBtn>
          </VCardText>
          <VDivider />
          <VCardText v-if="accessPreview">
            <VRow>
              <VCol cols="12" md="4">
                <h3 class="text-subtitle-1 mb-3">
                  Role Profile
                </h3>
                <VChip color="primary" variant="tonal">
                  {{ accessPreview.roleProfile || am("labels.noRoleProfile") }}
                </VChip>
              </VCol>

              <VCol cols="12" md="8">
                <h3 class="text-subtitle-1 mb-3">
                  Resolved Roles
                </h3>
                <div class="d-flex flex-wrap gap-2">
                  <VChip
                    v-for="role in accessPreview.roles"
                    :key="role"
                    :color="isDangerousRole(role) ? 'error' : 'secondary'"
                    variant="tonal"
                  >
                    {{ role }}
                  </VChip>
                </div>
              </VCol>

              <VCol cols="12">
                <h3 class="text-subtitle-1 mb-3">
                  Table Access
                </h3>
                <VDataTable
                  v-if="effectiveAccessTableRows.length"
                  :headers="effectiveAccessTableHeaders"
                  :items="effectiveAccessTableRows"
                  item-value="table"
                  :items-per-page="10"
                >
                  <template #item.table="{ item }">
                    <div class="font-weight-medium">
                      {{ item.table }}
                    </div>
                  </template>

                  <template #item.actions="{ item }">
                    <div class="d-flex flex-wrap gap-2 access-chip-stack">
                      <VChip
                        v-for="action in item.actions"
                        :key="`${item.table}-${action}`"
                        size="small"
                        color="secondary"
                        variant="tonal"
                      >
                        {{ action }}
                      </VChip>
                    </div>
                  </template>
                </VDataTable>
                <VAlert
                  v-if="!effectiveAccessTableRows.length"
                  type="info"
                  variant="tonal"
                  class="mt-4"
                >
                  No table permissions were returned by the backend.
                </VAlert>
              </VCol>

              <VCol cols="12">
                <h3 class="text-subtitle-1 mb-3">
                  Record Restrictions
                </h3>
                <VDataTable
                  v-if="effectiveAccessRestrictionRows.length"
                  :headers="effectiveAccessRestrictionHeaders"
                  :items="effectiveAccessRestrictionRows"
                  item-value="restriction"
                  :items-per-page="10"
                >
                  <template #item.restriction="{ item }">
                    <div class="font-weight-medium">
                      {{ item.restriction }}
                    </div>
                  </template>

                  <template #item.values="{ item }">
                    <div class="d-flex flex-wrap gap-2 access-chip-stack">
                      <VChip
                        v-for="value in item.values"
                        :key="`${item.restriction}-${value}`"
                        size="small"
                        color="secondary"
                        variant="tonal"
                      >
                        {{ value }}
                      </VChip>
                    </div>
                  </template>
                </VDataTable>
                <VAlert v-else type="info" variant="tonal">
                  No record-level restrictions were returned by the backend snapshot.
                </VAlert>
              </VCol>
            </VRow>
          </VCardText>
          <VCardText v-else>
            <VAlert type="warning" variant="tonal">
              The backend did not return an access snapshot.
            </VAlert>
          </VCardText>
        </VCard>
      </VWindowItem>
    </VWindow>

    <VDialog v-model="roleDialog" max-width="640" :fullscreen="isMobileShell" scrollable>
      <VCard>
        <VCardTitle>
          {{ am("actions.newRole") }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VTextField
            v-model="roleForm.role_name"
            :label="am('labels.roleName')"
            class="mb-4"
            autofocus
            :disabled="roleSaving"
          />

          <VRow>
            <VCol cols="12" md="6">
              <VSwitch
                v-model="roleForm.desk_access"
                :true-value="1"
                :false-value="0"
                color="primary"
                :label="am('labels.desk')"
                :disabled="roleSaving"
              />
            </VCol>
            <VCol cols="12" md="6">
              <VSwitch
                v-model="roleForm.disabled"
                :true-value="1"
                :false-value="0"
                color="primary"
                :label="am('labels.disabled')"
                :disabled="roleSaving"
              />
            </VCol>
          </VRow>

          <VTextField
            v-model="roleForm.home_page"
            :label="am('labels.homePage')"
            class="mb-4"
            :disabled="roleSaving"
          />

          <VTextField
            v-model="roleForm.restrict_to_domain"
            :label="am('labels.restrictDomain')"
            :disabled="roleSaving"
          />
        </VCardText>
        <VDivider />
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" :disabled="roleSaving" @click="closeRoleDialog">
            {{ am("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="roleSaving"
            :disabled="!canManageAccess"
            @click="saveRole"
          >
            {{ am("actions.save") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="userDialog" max-width="720" :fullscreen="isMobileShell" scrollable>
      <VCard>
        <VCardTitle>
          {{ am("actions.assign") }} {{ am("labels.roleProfiles") }} & {{ am("labels.roles") }}
        </VCardTitle>
        <VDivider />
        <VCardText v-if="selectedUser">
          <div class="mb-4">
            <div class="font-weight-medium">
              {{ getUserLabel(selectedUser) }}
            </div>
            <div class="text-body-2 text-medium-emphasis">
              {{ selectedUser.email || selectedUser.name }}
            </div>
          </div>

          <VAutocomplete
            v-model="selectedUserRoleProfiles"
            :items="roleProfileOptions"
            :label="am('labels.roleProfiles')"
            multiple
            chips
            closable-chips
            clearable
            hide-selected
            auto-select-first
            class="mb-4"
          />

          <VAutocomplete
            v-model="selectedUserRoles"
            :items="safeRoleOptions"
            :label="am('labels.roles')"
            multiple
            chips
            closable-chips
            clearable
            hide-selected
            auto-select-first
          />

          <VDivider class="my-5" />

          <h3 class="text-subtitle-1 mb-3">
            Resolved Roles Preview
          </h3>
          <div class="d-flex flex-wrap gap-2">
            <VChip
              v-for="role in selectedUserRolePreview"
              :key="role"
              size="small"
              :color="isDangerousRole(role) ? 'error' : 'secondary'"
              variant="tonal"
            >
              {{ role }}
            </VChip>
            <span v-if="!selectedUserRolePreview.length" class="text-medium-emphasis">
              No roles selected
            </span>
          </div>
        </VCardText>
        <VDivider />
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="closeUserDialog">
            {{ am("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :disabled="!canManageAccess"
            @click="saveUserRoleAssignments"
          >
            {{ am("actions.save") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="profileDialog" max-width="760" :fullscreen="isMobileShell" scrollable>
      <VCard>
        <VCardTitle>
          {{ profileDialogMode === "edit" ? t("alkokh.sidebar.permissionPage.editPermission") : am("actions.newRoleProfile") }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VTextField
            v-model="profileName"
            :label="t('alkokh.sidebar.permissionPage.permissionName')"
            class="mb-4"
          />
          <VAutocomplete
            v-model="profileRoles"
            :items="safeRoleOptions"
            :label="am('labels.roles')"
            multiple
            chips
            closable-chips
            clearable
            auto-select-first
          />
        </VCardText>
        <VDivider />
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="closeProfileDialog">
            {{ am("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :disabled="!canManageAccess"
            @click="saveRoleProfile"
          >
            {{ am("actions.save") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="deleteProfileDialog" max-width="520">
      <VCard>
        <VCardTitle>{{ am("actions.delete") }} {{ am("labels.accessProfile") }}</VCardTitle>
        <VCardText>
          Delete <strong>{{ profileToDelete?.role_profile || profileToDelete?.name }}</strong>?
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="deleteProfileDialog = false">
            {{ am("actions.cancel") }}
          </VBtn>
          <VBtn color="error" @click="confirmDeleteProfile">
            {{ am("actions.delete") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="addRuleDialog" max-width="560" :fullscreen="isMobileShell" scrollable>
      <VCard>
        <VCardTitle>
          {{ am("actions.addRule") }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VAutocomplete
            v-model="newRule.role"
            :items="permissionManagerRoles"
            item-title="label"
            item-value="value"
            :label="am('labels.role')"
            class="mb-4"
          />
          <VTextField
            v-model.number="newRule.permlevel"
            type="number"
            min="0"
            :label="am('labels.permissionLevel')"
          />
        </VCardText>
        <VDivider />
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="addRuleDialog = false">
            {{ am("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="permissionSaving"
            @click="addTablePermissionRule"
          >
            {{ am("actions.add") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="resetTableDialog" max-width="560">
      <VCard>
        <VCardTitle class="d-flex align-center gap-2">
          <VIcon icon="tabler-alert-triangle" color="warning" />
          {{ am("actions.resetTable") }}
        </VCardTitle>
        <VCardText>
          {{ am("messages.resetTableConfirm", { table: selectedPermissionTableName }) }}
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" :disabled="permissionSaving" @click="resetTableDialog = false">
            {{ am("actions.cancel") }}
          </VBtn>
          <VBtn
            color="warning"
            :loading="permissionSaving"
            @click="resetTablePermissions"
          >
            {{ am("actions.resetTable") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="userPermissionDialog" max-width="720" :fullscreen="isMobileShell" scrollable>
      <VCard>
        <VCardTitle>
          {{ am("actions.newUserPermission") }}
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VAutocomplete
            v-model="userPermissionForm.user"
            :items="userOptions"
            :label="am('labels.user')"
            class="mb-4"
          />
          <FrappeDoctypeAutocomplete
            v-model="allowedTableOption"
            doctype="DocType"
            :label="am('labels.allowedTable')"
            :placeholder="am('allowedTablePlaceholder')"
            class="mb-4"
            :hide-details="false"
            order-by="name asc"
          />
          <FrappeDoctypeAutocomplete
            v-model="allowedValueOption"
            :doctype="userPermissionForm.allow || 'Warehouse'"
            :label="am('labels.allowedValue')"
            :placeholder="`Search ${userPermissionForm.allow || 'record'}`"
            :disabled="!userPermissionForm.allow"
            :label-field="allowedValueLabelField"
            :fields="[allowedValueLabelField]"
            :search-fields="allowedValueSearchFields"
            :hide-details="false"
            class="mb-4"
          />
          <VAlert
            type="info"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ t("alkokh.accessManagement.userPermissionHelp") }}
          </VAlert>
          <VCheckbox
            v-model="userPermissionForm.apply_to_all_doctypes"
            :true-value="1"
            :false-value="0"
            :label="am('labels.allTables')"
          />
        </VCardText>
        <VDivider />
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="userPermissionDialog = false">
            {{ am("actions.cancel") }}
          </VBtn>
          <VBtn
            color="primary"
            :disabled="!canManageAccess"
            @click="saveUserPermission"
          >
            {{ am("actions.save") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="deleteUserPermissionDialog" max-width="520">
      <VCard>
        <VCardTitle>{{ am("actions.delete") }} {{ am("tabs.userPermissions") }}</VCardTitle>
        <VCardText>
          Remove access to <strong>{{ displayUserPermissionValue(userPermissionToDelete) }}</strong> for
          <strong>{{ userPermissionToDelete?.user }}</strong>?
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn variant="text" @click="deleteUserPermissionDialog = false">
            {{ am("actions.cancel") }}
          </VBtn>
          <VBtn color="error" @click="confirmDeleteUserPermission">
            {{ am("actions.delete") }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style scoped>
.access-management {
  max-width: 100%;
}

.access-search {
  max-inline-size: 360px;
}

.access-chip-stack {
  max-inline-size: 560px;
}

.access-path-stack {
  max-inline-size: 360px;
}

.access-role-select {
  min-inline-size: 260px;
}

.access-restriction-row {
  display: grid;
  align-items: center;
  gap: 12px;
  grid-template-columns: minmax(160px, 240px) 1fr;
}

@media (max-width: 700px) {
  .access-search {
    max-inline-size: 100%;
  }

  .access-restriction-row {
    grid-template-columns: 1fr;
  }
}
</style>
